import { Component, OnInit, Optional, Inject, OnDestroy } from "@angular/core";
import { BaseEditForm } from "src/app/shared/baseEditForm";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { IncaricoService } from "src/app/services/incarico.service";
import { ToastrService } from "ngx-toastr";
import {
  ActivatedRoute,
  ActivatedRouteSnapshot,
  Router,
} from "@angular/router";
import { findInRoute } from "src/app/shared/utilities";
import { Incarico } from "src/app/models/incarico";
import {
  MatDialog,
  MatDialogRef,
  MAT_DIALOG_DATA,
} from "@angular/material/dialog";
import { AuthService } from "src/app/services/auth.service";
import { User } from "src/app/models/user";
import * as moment from "moment";
import { filter, distinctUntilChanged } from "rxjs/operators";
import { WorkflowService } from "src/app/services/workflow.service";
import { WorkflowStep } from "src/app/models/workflow-step";
import { EstensioneService } from "src/app/services/estensione.service";
import { extensions } from "src/app/admin/estensioni/config";
import { Estensione } from "src/app/models/estensione";
import { MatSnackBar } from "@angular/material/snack-bar";
import * as ErrorHandler from "./errors";
import { HttpClient } from "@angular/common/http";
import { DeepPropertyService } from "../../services/deep-property.service";
import { FileService } from "../../services/file.service";
import { RamoService } from "../../services/ramo.service";
import { UserService } from "src/app/services/user.service";
import { ComuneService } from "src/app/services/comune.service";
import { PolizzaService } from "src/app/services/polizza.service";
import { File as IncaricoFile } from "src/app/models/file";
import { DateInvioAttoHistoryDialogComponent } from "./date-invio-atto-history-dialog/date-invio-atto-history-dialog.component";
import { DateSollecitoDocumentazioneHistoryDialogComponent } from "./date-sollecito-documentazione-history-dialog/date-sollecito-documentazione-history-dialog.component";
import { PreferenzaAgenziaService } from "src/app/services/preferenza-agenzia.service";
import { PreferenzaAmministratoreService } from "src/app/services/preferenza-amministratore.service";

@Component({
  selector: "form-incarichi",
  templateUrl: "./form.component.html",
  styleUrls: ["./form.component.scss"],
})
export class FormIncarichiComponent
  extends BaseEditForm
  implements OnInit, OnDestroy
{
  readOnlyForm: FormGroup; //only for displaying infos
  private contraenteMissingFieldsState: string[] = [];
  dataSopralluogoDisabledReason: string | null = null;

  /** stores the original routeReuseStrategy so that we can use it again once this component gets destroyed */
  oldRouteReuseStrategy: (
    future: ActivatedRouteSnapshot,
    curr: ActivatedRouteSnapshot
  ) => boolean;

  isInseritoDaPortale = false;

  //Valorizzato solo se sta venendo editato un incarico già esistente.
  incarico: Incarico;

  private skipNavigationCheckOnce = false;

  comuniByName: Map<string, any> = new Map();
  loading: boolean = false;
  form: FormGroup;
  incaricatoPreferenceHints: { [userId: number]: string[] } = {};
  private preferenceHintsRequestId = 0;

  constructor(
    private fb: FormBuilder,
    private incaricoService: IncaricoService,
    private toast: ToastrService,
    @Optional() public dialogRef: MatDialogRef<FormIncarichiComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: Incarico,
    private dialog: MatDialog,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private authService: AuthService,
    private _snackBar: MatSnackBar,
    private estensioneService: EstensioneService,
    private workflowService: WorkflowService,
    private http: HttpClient,
    private deepPropertyService: DeepPropertyService,
    private fileService: FileService,
    private ramoService: RamoService,
    private userService: UserService,
    private comuneService: ComuneService,
    private polizzaService: PolizzaService,
    private preferenzaAgenziaService: PreferenzaAgenziaService,
    private preferenzaAmministratoreService: PreferenzaAmministratoreService
  ) {
    super();
    if (this.data) this.incarico = this.data;

    this.oldRouteReuseStrategy =
      this.router.routeReuseStrategy.shouldReuseRoute;

    /** fixes angular not reloading component when user clicks on pregressi id. REMEMBER TO SET the shouldReuseRoute
     * back to its original value, otherwise you could be breaking other parts of the application that rely
     * on a custom shouldReuseRoute.
     */
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;
  }

  setDataInvioAttoToday() {
    try {
      this.form?.get("data_invio_atto")?.setValue(moment());
    } catch {}
  }

  async toggleDataInvioAtto(checked: boolean) {
    if (checked) {
      // here
      // On enabling, ensure the incarico is removed from workflow step 90
      try {
        await this.removeWorkflowStep([90]);
      } catch (e) {
        console.error("Errore rimozione step 90:", e);
      }
      this.setDataInvioAttoToday();
      await this.handleSuccessfulSubmit();
    } else {
      this.form?.get("data_invio_atto")?.setValue(null);
    }
  }

  // Used by template to show days since the FIRST ever recorded "Data Invio Atto"
  // for this incarico (historical earliest), independent of current form value
  getUltimaModificaInvioAttoFromForm(): number {
    try {
      // Prefer the earliest historical value if provided by backend
      const first = (this.incarico as any)?.data_invio_atto_first_by_created as
        | moment.Moment
        | Date
        | string
        | null
        | undefined;

      let m: moment.Moment | null = null;
      if (moment.isMoment(first)) {
        m = first as moment.Moment;
      } else if (first instanceof Date) {
        m = moment(first);
      } else if (typeof first === "string" && first.trim()) {
        m = moment(first, ["DD/MM/YYYY", moment.ISO_8601, "YYYY-MM-DD"], true);
        if (!m.isValid()) m = moment(first);
      }

      // Fallback: use current incarico value, then form control value
      if (!m || !m.isValid()) {
        const iv = this.incarico?.data_invio_atto as any;
        if (moment.isMoment(iv)) m = iv as moment.Moment;
        else if (iv instanceof Date) m = moment(iv);
        else if (typeof iv === "string" && iv.trim()) {
          m = moment(iv, ["DD/MM/YYYY", moment.ISO_8601, "YYYY-MM-DD"], true);
          if (!m.isValid()) m = moment(iv);
        } else {
          const ctrl = this.form?.get("data_invio_atto");
          const v = ctrl ? ctrl.value : null;
          if (moment.isMoment(v)) m = v as moment.Moment;
          else if (v instanceof Date) m = moment(v);
          else if (typeof v === "string" && v.trim()) {
            m = moment(v, ["DD/MM/YYYY", moment.ISO_8601, "YYYY-MM-DD"], true);
            if (!m.isValid()) m = moment(v);
          }
        }
      }

      if (m && m.isValid()) return moment().diff(m, "days");
      return this.incarico?.ultimaModificaInvioAtto || 0;
    } catch {}
    return this.incarico?.ultimaModificaInvioAtto || 0;
  }

  getAccertatoreModifiedAtLabel(): string | null {
    try {
      const rawValue = (this.incarico as any)?.accertatore_modified_at;
      if (!rawValue) {
        return null;
      }

      let m: moment.Moment | null = null;
      if (moment.isMoment(rawValue)) {
        m = rawValue as moment.Moment;
      } else if (rawValue instanceof Date) {
        m = moment(rawValue);
      } else if (typeof rawValue === "string" && rawValue.trim()) {
        m = moment(
          rawValue,
          ["DD/MM/YYYY HH:mm", moment.ISO_8601, "YYYY-MM-DD HH:mm:ss"],
          true
        );
        if (!m.isValid()) {
          m = moment(rawValue);
        }
      }

      if (!m || !m.isValid()) {
        return null;
      }

      return `Ultima modifica: ${m.format("DD/MM/YYYY HH:mm")}`;
    } catch {
      return null;
    }
  }

  openDateInvioAttoHistory(event?: MouseEvent): void {
    event?.stopPropagation();
    if (!this.incarico?.id) {
      this.toast.info(
        "Salva l'incarico per poter visualizzare lo storico delle date."
      );
      return;
    }

    const dialogRef = this.dialog.open(DateInvioAttoHistoryDialogComponent, {
      width: "520px",
      data: {
        incaricoId: this.incarico.id,
        incaricoLabel: this.incarico.humanIdentifier,
        onUpdate: (count: number, earliest: moment.Moment | null) => {
          if (this.incarico) {
            this.incarico.date_invio_atto_count = count;
            this.incarico.data_invio_atto_first_by_created = earliest || null;
          }
        },
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (
        result &&
        typeof result.remainingCount === "number" &&
        this.incarico
      ) {
        this.incarico.date_invio_atto_count = result.remainingCount;
        if (result.earliestValore) {
          const earliest = moment(result.earliestValore);
          this.incarico.data_invio_atto_first_by_created = earliest.isValid()
            ? earliest
            : null;
        } else {
          this.incarico.data_invio_atto_first_by_created = null;
        }
      }
    });
  }

  // --- Sollecito Documentazione methods (mirror of Data Invio Atto) ---

  setSollecitoDocumentazioneToday() {
    try {
      this.form?.get("data_sollecito_documentazione")?.setValue(moment());
    } catch {}
  }

  async toggleSollecitoDocumentazione(checked: boolean) {
    if (checked) {
      this.setSollecitoDocumentazioneToday();
      await this.handleSuccessfulSubmit();
    } else {
      this.form?.get("data_sollecito_documentazione")?.setValue(null);
    }
  }

  getUltimaModificaSollecitoDocumentazioneFromForm(): number {
    try {
      const first = (this.incarico as any)?.data_sollecito_documentazione_first_by_created as
        | moment.Moment
        | Date
        | string
        | null
        | undefined;

      let m: moment.Moment | null = null;
      if (moment.isMoment(first)) {
        m = first as moment.Moment;
      } else if (first instanceof Date) {
        m = moment(first);
      } else if (typeof first === "string" && first.trim()) {
        m = moment(first, ["DD/MM/YYYY", moment.ISO_8601, "YYYY-MM-DD"], true);
        if (!m.isValid()) m = moment(first);
      }

      if (!m || !m.isValid()) {
        const iv = this.incarico?.data_sollecito_documentazione as any;
        if (moment.isMoment(iv)) m = iv as moment.Moment;
        else if (iv instanceof Date) m = moment(iv);
        else if (typeof iv === "string" && iv.trim()) {
          m = moment(iv, ["DD/MM/YYYY", moment.ISO_8601, "YYYY-MM-DD"], true);
          if (!m.isValid()) m = moment(iv);
        } else {
          const ctrl = this.form?.get("data_sollecito_documentazione");
          const v = ctrl ? ctrl.value : null;
          if (moment.isMoment(v)) m = v as moment.Moment;
          else if (v instanceof Date) m = moment(v);
          else if (typeof v === "string" && v.trim()) {
            m = moment(v, ["DD/MM/YYYY", moment.ISO_8601, "YYYY-MM-DD"], true);
            if (!m.isValid()) m = moment(v);
          }
        }
      }

      if (m && m.isValid()) return moment().diff(m, "days");
      return this.incarico?.ultimaModificaSollecitoDocumentazione || 0;
    } catch {}
    return this.incarico?.ultimaModificaSollecitoDocumentazione || 0;
  }

  openDateSollecitoDocumentazioneHistory(event?: MouseEvent): void {
    event?.stopPropagation();
    if (!this.incarico?.id) {
      this.toast.info(
        "Salva l'incarico per poter visualizzare lo storico delle date."
      );
      return;
    }

    const dialogRef = this.dialog.open(DateSollecitoDocumentazioneHistoryDialogComponent, {
      width: "520px",
      data: {
        incaricoId: this.incarico.id,
        incaricoLabel: this.incarico.humanIdentifier,
        onUpdate: (count: number, earliest: moment.Moment | null) => {
          if (this.incarico) {
            this.incarico.date_sollecito_documentazione_count = count;
            this.incarico.data_sollecito_documentazione_first_by_created = earliest || null;
          }
        },
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (
        result &&
        typeof result.remainingCount === "number" &&
        this.incarico
      ) {
        this.incarico.date_sollecito_documentazione_count = result.remainingCount;
        if (result.earliestValore) {
          const earliest = moment(result.earliestValore);
          this.incarico.data_sollecito_documentazione_first_by_created = earliest.isValid()
            ? earliest
            : null;
        } else {
          this.incarico.data_sollecito_documentazione_first_by_created = null;
        }
      }
    });
  }


  ngOnDestroy(): void {
    this.router.routeReuseStrategy.shouldReuseRoute =
      this.oldRouteReuseStrategy;
  }

  loggedInUser: User;
  extension: Estensione;
  deepPropertyEnabled: boolean;
  externalExists: boolean;
  fileUrl: string;
  hasAmministrativo: boolean;

  private tipoDannoRamoMapping = {
    4: 19,
    6: 11,
    8: 1,
    11: 11,
    14: 11,
    19: 11,
    23: 11,
    24: 11,
    17: 14,
    39: 20,
    18: 15,
    21: 16,
    9: 12,
  };

  // Returns true if the incarico is currently in the workflow step 110 ("Atto da Inviare")
  // and no "data_invio_atto" is set (mutually exclusive UI states)
  private isAttoDaInviareActive(): boolean {
    try {
      if (!this.incarico) return false;
      if (this.incarico?.data_invio_atto) return false;
      const steps = (this.incarico as any)?.workflow_steps || [];
      return Array.isArray(steps) && steps.some((s: any) => +s?.id === 110);
    } catch {
      return false;
    }
  }

  async ngOnInit() {
    let routerState = window.history.state;
    this.isInseritoDaPortale = routerState?.inseritoDaPortale;
    this.loggedInUser = this.authService.loggedInUser.getValue();

    this.hasAmministrativo = this.loggedInUser.hasRole("amministrativo");

    this.fileUrl =
      "frontend/src/assets/DeepProperty_Report_nanalisi_9650__date_20240917_162751_(1).pdf";

    if (!this.incarico) {
      let idIncarico = await findInRoute("idIncarico", this.activatedRoute);
      if (idIncarico) {
        this.incarico = await this.incaricoService.getOne(+idIncarico, [
          "stato",
          "comune",
          "tipo_danno",
          "ramo",
          "stato_videoperizia",
          "stato_riparazione",
          "polizza",
          "polizza.ramo",
          "polizza.compagnia",
          "polizza.compagnia.gruppo",
          "polizza.contraente",
          "polizza.contraente.comune",
          "polizza.agenzia",
          // Needed to build mail subject with Controparte name
          "controparti",
          "controparti.soggetto",
          "workflow_steps",
          "ispettorato",
          "studio",
          "inseritore",
          "accertatore",
          "firmatario",
          "incaricato",
          "auditor",
          "evasore",
          "liquidatore",
        ]);

        if (this.incarico) {
          if (this.incarico.data_sopralluogo_fissata_data === undefined) {
            this.incarico.data_sopralluogo_fissata_data = null;
          }
          if (this.incarico.data_sopralluogo_fissata_ora === undefined) {
            this.incarico.data_sopralluogo_fissata_ora = null;
          }
        }

        /*try {
          this.extension = await this.estensioneService.bySlug(extensions.generali);
          this.externalExists = await this.estensioneService.getExternalAssignmentExists(this.incarico.numero_sinistro);
        }
        catch(e) {
          console.log("Errore, configurazione o incarico non trovati: " + e);
        }*/

        this.estensioneService
          .bySlug(extensions.generali)
          .then((extension) => {
            this.extension = extension;
          })
          .catch(() => {
            console.log("No extension");
          });

        this.estensioneService
          .bySlug(extensions.deep_property)
          .then((extension) => {
            this.deepPropertyEnabled = extension?.enabled;
          });

        this.estensioneService
          .getExternalAssignmentExists(this.incarico.numero_sinistro ?? "")
          .then((externaExists) => {
            this.externalExists = externaExists;
            console.log(this.externalExists);
          })
          .catch(() => {
            this.externalExists = false;
            console.log("Niente incarico");
          });
      }
    }
    let defaultNote = "";
    const numeroSinistro = this.incarico?.numero_sinistro;
    const sinistroSegment = numeroSinistro?.substring(3, 6);
    if (
      sinistroSegment &&
      ["058", "480", "T93", "141"].includes(sinistroSegment)
    ) {
      defaultNote = "PAGAMENTO AGENZIALE";
    }

    this.form = this.fb.group({
      stato: [this.incarico?.stato],
      accertatore: [this.incarico?.accertatore],
      inseritore: [
        this.incarico?.inseritore ?? this.authService.loggedInUser.getValue(),
      ],
      incaricato: [
        this.incarico?.incaricato ?? this.authService.loggedInUser.getValue(),
      ],
      firmatario: [this.incarico?.firmatario],
      auditor: [this.incarico?.auditor],
      evasore: [this.incarico?.evasore],
      liquidatore: [this.incarico?.liquidatore],
      data: [this.incarico?.data ?? moment()],
      garanzia: [this.incarico?.garanzia],
      ispettorato: [this.incarico?.ispettorato],
      data_primo_contatto: [this.incarico?.data_primo_contatto],
      data_sopralluogo_fissata_data: [
        this.incarico?.data_sopralluogo_fissata_data || null,
      ],
      data_sopralluogo_fissata_ora: [
        this.toTimeInput(this.incarico?.data_sopralluogo_fissata_ora),
      ],
      data_sopralluogo: [this.incarico?.data_sopralluogo],
      data_secondo_sopralluogo: [this.incarico?.data_secondo_sopralluogo],

      prima_interlocutoria: [this.incarico?.prima_interlocutoria],
      seconda_interlocutoria: [this.incarico?.seconda_interlocutoria],
      terza_interlocutoria: [this.incarico?.terza_interlocutoria],

      pronta_liquidazione: [this.incarico?.pronta_liquidazione],
      over_10000: [this.incarico?.over_10000],
      over_50000: [this.incarico?.over_50000],
      globale_fabbricati: [this.incarico?.globale_fabbricati],
      concordato: [this.incarico?.concordato],
      riserva: [this.incarico?.riserva],
      foto_attestati_danno: [this.incarico?.foto_attestati_danno],
      documento_contabile_opere_ripristino: [
        this.incarico?.documento_contabile_opere_ripristino,
      ],
      sollecitato: [this.incarico?.sollecitato],
      // esclusione_interlocutoria is hidden and not bound anymore in UI
      esclusione_interlocutoria: [this.incarico?.esclusione_interlocutoria],
      // Initialize from workflow: true if incarico is in step 110 ("Atto da Inviare") and there's no data_invio_atto
      inviare_atto: [this.isAttoDaInviareActive()],

      riserva_di_danno: [this.incarico?.riserva_di_danno],
      riserva_di_danno_range: [
        this.incarico?.riserva_di_danno_range == "" ||
        this.incarico?.riserva_di_danno_range == null
          ? this.incarico?.riserva_di_danno
          : this.incarico?.riserva_di_danno_range,
      ],
      danno_accertato: [this.incarico?.danno_accertato],
      supplementi_indennizzo: [this.incarico?.supplementi_indennizzo],
      spese_demolizione_sgombero: [this.incarico?.spese_demolizione_sgombero],
      importo_richiesto: [this.incarico?.importo_richiesto],
      indennizzo_risarcimento: [this.incarico?.indennizzo_risarcimento],
      data_spedizione: [this.incarico?.data_spedizione],
      data_invio_atto: [this.incarico?.data_invio_atto],
      data_sollecito_documentazione: [this.incarico?.data_sollecito_documentazione],
      studio: [this.incarico?.studio ?? this.loggedInUser.studio],
      broker: [this.incarico?.broker],

      numero_sinistro: [this.incarico?.numero_sinistro],
      data_sinistro: [this.incarico?.data_sinistro],
      data_denuncia: [this.incarico?.data_denuncia],
      data_scadenza: [this.incarico?.data_scadenza],
      polizza: [this.incarico?.polizza],
      comune: [this.incarico?.comune],
      via: [this.incarico?.via],
      civico: [this.incarico?.civico],
      cap: [this.incarico?.cap],
      tipo_danno: [this.incarico?.tipo_danno],
      ramo: [this.incarico?.ramo],

      stato_riparazione: [this.incarico?.stato_riparazione],
      stato_videoperizia: [this.incarico?.stato_videoperizia],

      note: [this.incarico?.note || defaultNote],
    });

    this.readOnlyForm = this.fb.group({
      contraente: [this.incarico?.polizza?.contraente],
      amministratore: [this.incarico?.amministratore],
      agenzia: [this.incarico?.polizza?.agenzia],
    });

    this.setupContraenteSync();
    this.setupViaCapitalization();
    this.setupAutoGlobaleFabbricatiToggle();
    this.triggerRefreshIncaricatoPreferenceHints(
      this.form?.value?.polizza,
      this.readOnlyForm?.value?.amministratore
    );

    // Enrich date_invio_atto_count from Workflow (ensure consistency with Scrivania Digitale)
    this.enrichDateInvioAttoCountFromWorkflow();

    // Observe incaricato changes to possibly auto-set accertatore (id=1019) when incaricato is 201
    // Do not auto-apply on initial load
    this.setupAutoAccertatoreOnIncaricato();

    // --- Restrizione accertatore: SEMPRE attiva ---
    const loggedUserId = this.loggedInUser?.id;
    const incaricatoId =
      this.incarico?.incaricato?.id ??
      this.incarico?.incaricato_id ??
      this.form?.value?.incaricato?.id ??
      null;
    const isAdmin = this.loggedInUser.hasRole("amministratore");
    const isAmministrativo = this.loggedInUser.hasRole("amministrativo");
    const isIncaricato = loggedUserId != null && loggedUserId === incaricatoId;

    if (!isAdmin && !isAmministrativo && !isIncaricato) {
      this.form.controls.accertatore.disable();
    }

    // Reagire ai cambi di incaricato: rivalutare enable/disable accertatore
    if (!isAdmin && !isAmministrativo) {
      this.form.get("incaricato")?.valueChanges.subscribe((newIncaricato: any) => {
        const newIncaricatoId = newIncaricato?.id ?? null;
        if (loggedUserId != null && loggedUserId === newIncaricatoId) {
          this.form.controls.accertatore.enable();
        } else {
          this.form.controls.accertatore.disable();
        }
      });
    }

    // --- Restrizione estensione per campo incaricato (preservata) ---
    const disabledWhenConcluded = (
      await this.estensioneService.bySlug("Disabilitare Accertatore Incaricato")
    ).enabled;
    if (
      disabledWhenConcluded &&
      (this.incarico?.stato?.id == 5 || this.incarico?.data_sopralluogo != null)
    ) {
      if (!isAdmin) {
        this.form.controls.incaricato.disable();
      }
    }

    if (
      !this.loggedInUser.hasRole("Può modificare data sopralluogo") &&
      this.incarico.data_sopralluogo
    )
      this.form.get("data_sopralluogo")?.disable();

    // Disable 'data_sopralluogo' unless 'data_primo_contatto' is filled in
    this.setupDataSopralluogoDependency();

    this.form
      .get("prima_interlocutoria")
      ?.valueChanges.subscribe((dateValue) => {
        if (dateValue) {
          this.checkRiservaOnDateChange("prima_interlocutoria", dateValue);
        }
      });

    this.form
      .get("seconda_interlocutoria")
      ?.valueChanges.subscribe((dateValue) => {
        if (dateValue) {
          this.checkRiservaOnDateChange("seconda_interlocutoria", dateValue);
        }
      });

    this.form
      .get("terza_interlocutoria")
      ?.valueChanges.subscribe((dateValue) => {
        if (dateValue) {
          this.checkRiservaOnDateChange("terza_interlocutoria", dateValue);
        }
      });

    this.form
      .get("riserva_di_danno_range")
      ?.valueChanges.subscribe((riservaValue) => {
        if (riservaValue) {
          this.checkOver50000OnRiservaChange(riservaValue);
        }
      });

    this.setupInteractionBetweenDataSopralluogoAndRiservaDiDanno();
    this.setupTipoDannoRamoMapping();
    this.setupCheckboxNoteUpdates();
    this.setupDataScadenzaObserver();
    this.setupConcordatoBehaviour();

    // Keep Invare Atto toggle mutually exclusive with data_invio_atto
    // Run only when the value is a valid date (avoid firing on partial manual typing)
    this.form
      .get("data_invio_atto")
      ?.valueChanges.pipe(
        filter((v: any) => moment.isMoment(v) && v.isValid()),
        distinctUntilChanged((a: any, b: any) =>
          moment.isMoment(a) && moment.isMoment(b)
            ? a.isSame(b, "day")
            : a === b
        )
      )
      .subscribe(async () => {
        // Turn off the toggle without firing its own handler
        this.form.get("inviare_atto")?.setValue(false, { emitEvent: false });
        // Silently remove workflow steps and save (no toast here — the save toast comes from handleSuccessfulSubmit)
        this.currentlyWorkingWith[110] = true;
        try {
          for (const id of [90, 100, 110]) {
            await this.workflowService.removeFromList(
              this.incarico,
              new WorkflowStep().deserialize({ id })
            );
          }
          if (this.incarico?.id) {
            await this.saveCurrentIncaricoSilently();
            this.toast.success("Incarico aggiunto a 'Atto Inviato'");
          }
        } finally {
          this.currentlyWorkingWith[110] = false;
        }
      });

    // When data_sollecito_documentazione is set, remove from waiting steps and save
    this.form
      .get("data_sollecito_documentazione")
      ?.valueChanges.pipe(
        filter((v: any) => moment.isMoment(v) && v.isValid()),
        distinctUntilChanged((a: any, b: any) =>
          moment.isMoment(a) && moment.isMoment(b)
            ? a.isSame(b, "day")
            : a === b
        )
      )
      .subscribe(async () => {
        try {
          for (const id of [90, 100]) {
            await this.workflowService.removeFromList(
              this.incarico,
              new WorkflowStep().deserialize({ id })
            );
          }
          if (this.incarico?.id) {
            // Append "Sollecito Documentazione [date]" to notes
            const dateValue = this.form.get("data_sollecito_documentazione")?.value;
            if (dateValue) {
              const currentNote = this.form.get("note")?.value || "";
              const newNote = `${currentNote}${currentNote ? "\n" : ""}Sollecito Documentazione ${this.formatDate(dateValue)}`;
              this.form.patchValue({ note: newNote });
            }

            await this.saveCurrentIncaricoSilently();

            // Update count locally so the UI reflects the change immediately
            if (this.incarico) {
              if (!this.incarico.date_sollecito_documentazione_count) {
                (this.incarico as any).data_sollecito_documentazione_first_by_created =
                  this.form.get("data_sollecito_documentazione")?.value;
              }
              this.incarico.date_sollecito_documentazione_count =
                (this.incarico.date_sollecito_documentazione_count || 0) + 1;
            }

            this.toast.success("Incarico aggiunto a 'Sollecito Documentazione'");
          }
        } catch (e) {
          console.error(e);
        }
      });

    // On toggle, add/remove from workflow step 110
    this.form
      .get("inviare_atto")
      ?.valueChanges.subscribe(async (checked: boolean) => {
        this.currentlyWorkingWith[110] = true;
        try {
          if (checked && !this.form?.value?.data_invio_atto) {
            await this.addToWorkflowStep(110);
            await this.removeWorkflowStep([90, 100]);
          } else if (!checked) {
            await this.removeWorkflowStep([110]);
          }
          // Auto-save incarico after toggling
          if (this.incarico?.id) {
            await this.saveCurrentIncaricoSilently();
          }
        } catch (e) {
          console.error(e);
        } finally {
          this.currentlyWorkingWith[110] = false;
        }
      });

  }

  private async enrichDateInvioAttoCountFromWorkflow(): Promise<void> {
    try {
      if (!this.incarico?.id) return;
      const studioId = this.loggedInUser?.studio_id;
      const steps: any[] = await this.workflowService
        .getWorkflow({}, studioId)
        .toPromise();
      for (const s of steps || []) {
        const inc = (s?.incarichi || []).find(
          (x: any) => x?.id === this.incarico.id
        );
        if (inc && typeof inc.date_invio_atto_count === "number") {
          this.incarico.date_invio_atto_count = inc.date_invio_atto_count;
          break;
        }
      }
    } catch {}
  }

  // When incaricato is user id 201, set accertatore to user id 1019
  private setupAutoAccertatoreOnIncaricato(): void {
    const SPECIAL_INCARICATO_ID = 201;
    const SPECIAL_ACCERTATORE_ID = 1019;

    const incaricatoCtrl = this.form?.get("incaricato");
    const accertatoreCtrl = this.form?.get("accertatore");
    if (!incaricatoCtrl || !accertatoreCtrl) return;

    const maybeApplyRule = async (user: User | null | undefined) => {
      try {
        if (user?.id === SPECIAL_INCARICATO_ID) {
          const currentAcc = accertatoreCtrl.value as User | null | undefined;
          // Apply auto-set ONLY if accertatore is currently null/undefined
          if (!currentAcc) {
            const acc = await this.userService.getOne(SPECIAL_ACCERTATORE_ID);
            this.form.patchValue({ accertatore: acc });
          }
        }
      } catch (e) {
        console.error("Errore auto-set accertatore:", e);
      }
    };

    // React only to subsequent changes (no initial auto-apply)
    incaricatoCtrl.valueChanges.subscribe((value: User) => {
      maybeApplyRule(value);
    });
  }

  get showConcordatoToggle(): boolean {
    try {
      const polizza = this.resolveSelectedPolizzaForConcordato();
      const compagnia =
        polizza?.compagnia?.original_model ??
        polizza?.compagnia ??
        null;
      return compagnia?.concordato_visibile === true;
    } catch {
      return false;
    }
  }

  private resolveSelectedPolizzaForConcordato(): any {
    if (!this.form) {
      return this.incarico?.polizza ?? null;
    }
    const ctrlValue = this.form.get("polizza")?.value ?? null;
    const normalized = ctrlValue?.original_model ?? ctrlValue ?? null;
    if (normalized?.original_model) {
      return normalized.original_model;
    }
    return normalized;
  }

  private setupConcordatoBehaviour(): void {
    const polizzaCtrl = this.form?.get("polizza");
    if (!polizzaCtrl) {
      return;
    }

    this.resetConcordatoIfHidden(false);

    polizzaCtrl.valueChanges.subscribe(() => {
      this.resetConcordatoIfHidden();
    });
  }

  private resetConcordatoIfHidden(emitEvent: boolean = true): void {
    const concordatoCtrl = this.form?.get("concordato");
    if (!concordatoCtrl) {
      return;
    }

    if (!this.showConcordatoToggle && !!concordatoCtrl.value) {
      concordatoCtrl.setValue(false, { emitEvent });
    }
  }

  get canCopyAddressFromPolizza(): boolean {
    try {
      const roles = this.loggedInUser?.ruoli || [];
      return roles.some((r) => [1, 18].includes(r.id));
    } catch {
      return false;
    }
  }

  get canSeeAttesaDoc(): boolean {
    try {
      const roles = this.loggedInUser?.ruoli || [];
      return roles.some((r) => [1, 18].includes(r.id));
    } catch {
      return false;
    }
  }

  currentlyWorkingWith = {
    70: false,
    100: false,
    110: false,
    120: false,
  };

  private async saveCurrentIncaricoSilently(): Promise<void> {
    try {
      Object.assign(this.incarico, this.form.value);
      await this.incaricoService.edit(this.incarico);
    } catch (e) {
      console.error("Errore salvataggio automatico:", e);
    }
  }

  private setupCheckboxNoteUpdates() {
    const checkboxNoteMapping = {
      foto_attestati_danno: "Foto attestati il danno",
      documento_contabile_opere_ripristino:
        "Doc. contabile opere di ripristino",
    };

    Object.entries(checkboxNoteMapping).forEach(([fieldName, noteText]) => {
      this.form.get(fieldName)?.valueChanges.subscribe((isChecked) => {
        this.updateNoteForCheckbox(isChecked, noteText);
      });
    });
  }

  private updateNoteForCheckbox(isChecked: boolean, noteText: string) {
    const currentNote = this.form.get("note")?.value || "";

    if (isChecked) {
      if (!currentNote.includes(noteText)) {
        const newNote = `${currentNote}${currentNote ? "\n" : ""}${noteText}`;
        this.form.patchValue({ note: newNote });
      }
    } else {
      const newNote = currentNote
        .split("\n")
        .filter((line) => line.trim() !== noteText)
        .join("\n")
        .replace(/^\n+|\n+$/g, "");

      this.form.patchValue({ note: newNote });
    }
  }

  private setupTipoDannoRamoMapping() {
    this.form.controls.tipo_danno.valueChanges.subscribe(async (tipoDanno) => {
      if (tipoDanno && tipoDanno.id) {
        const ramoMappato = this.tipoDannoRamoMapping[tipoDanno.id];

        if (ramoMappato) {
          try {
            const ramoCompleto = await this.ramoService.getOne(ramoMappato);
            this.form.patchValue({
              ramo: ramoCompleto,
            });
          } catch (error) {
            console.error("Errore nel recupero del ramo:", error);
            this.form.patchValue({
              ramo: { id: ramoMappato },
            });
          }
        }
      }
    });
  }

  async provaTables() {
    let data = {
      data: ["modalita_pagamento", "tipo_garanzia", "tipo_esito_danno"],
    };
    let res = await this.estensioneService.retrieveTablesFromGenerali(data);
    console.log(res);
  }

  async addToWorkflowStep(id) {
    this.currentlyWorkingWith[id] = true;
    try {
      await this.workflowService.addToList(
        this.incarico,
        new WorkflowStep().deserialize({ id })
      );
      // Success toast for specific workflow steps
      if (id === 100)
        this.toast.success("Incarico aggiunto a 'Attesa Documentazione'");
      if (id === 110)
        this.toast.success("Incarico aggiunto a 'Atto da Inviare'");
      if (id === 120) this.toast.success("Incarico aggiunto a 'Da Redigere'");
    } finally {
      this.currentlyWorkingWith[id] = false;
    }
  }

  async removeWorkflowStep(ids: number[]) {
    const idsArray = Array.isArray(ids) ? ids : [ids];
    if (!idsArray.length) return;

    idsArray.forEach((id) => (this.currentlyWorkingWith[id] = true));
    try {
      for (const id of idsArray) {
        await this.workflowService.removeFromList(
          this.incarico,
          new WorkflowStep().deserialize({ id })
        );
        // Success toast on removal as well for clarity
        if (id === 100)
          this.toast.success("Incarico rimosso da 'Attesa Documentazione'");
        if (id === 110)
          this.toast.success("Incarico rimosso da 'Atto da Inviare'");
        if (id === 120) this.toast.success("Incarico rimosso da 'Da Redigere'");
      }
    } finally {
      idsArray.forEach((id) => (this.currentlyWorkingWith[id] = false));
    }
  }

  private checkOver50000OnRiservaChange(riservaValue: any): void {
    if (!riservaValue || typeof riservaValue !== "string") {
      return;
    }

    const trimmedValue = riservaValue.trim();

    // Estrai il primo numero (minimo nel caso di range)
    let numberToCheck = "";

    // Verifica se è un range con trattino
    const dashMatch = trimmedValue.match(/^(\d{1,3}(?:\.\d{3})*,\d{2})\s*-\s*/);
    if (dashMatch) {
      numberToCheck = dashMatch[1];
    }
    // Verifica se è un range con slash
    else {
      const slashMatch = trimmedValue.match(
        /^(\d{1,3}(?:\.\d{3})*,\d{2})\s*\/\s*/
      );
      if (slashMatch) {
        numberToCheck = slashMatch[1];
      } else {
        // È un numero singolo
        numberToCheck = trimmedValue;
      }
    }

    // Converti il numero in formato numerico
    const numericValue = this.parseItalianNumber(numberToCheck);

    // Imposta i flag basati sul valore della riserva
    if (numericValue >= 50000) {
      this.form.patchValue({ over_50000: true, over_10000: false });
    } else if (numericValue >= 10000) {
      this.form.patchValue({ over_50000: false, over_10000: true });
    } else if (numericValue > 0) {
      // Solo se è un numero valido > 0
      this.form.patchValue({ over_50000: false, over_10000: false });
    }
  }

  isRiservaDannoFormatValid(): boolean {
    const value = this.form.value?.riserva_di_danno_range;

    if (value == null || value == "") return true;

    // Regex per validare il formato numerico con punti per migliaia e virgola per decimali
    const numberPattern = /^\d{1,3}(?:\.\d{3})*,\d{2}$/;

    // Trim della stringa per rimuovere spazi iniziali/finali
    const trimmedValue = value.trim();

    // Verifica se è un singolo numero
    if (numberPattern.test(trimmedValue)) {
      return true;
    }

    // Verifica se è un range con trattino (con o senza spazi)
    const dashRangePattern =
      /^(\d{1,3}(?:\.\d{3})*,\d{2})\s*-\s*(\d{1,3}(?:\.\d{3})*,\d{2})$/;
    if (dashRangePattern.test(trimmedValue)) {
      return true;
    }

    // Verifica se è un range con slash (con o senza spazi)
    const slashRangePattern =
      /^(\d{1,3}(?:\.\d{3})*,\d{2})\s*\/\s*(\d{1,3}(?:\.\d{3})*,\d{2})$/;
    if (slashRangePattern.test(trimmedValue)) {
      return true;
    }

    return false;
  }

  private toTimeInput(value: any): string | null {
    if (value === null || value === undefined || value === "") {
      return null;
    }

    if (typeof value === "string") {
      return value;
    }

    if (moment.isMoment(value)) {
      return value.clone().format("HH:mm");
    }

    if (value instanceof Date) {
      return moment(value).format("HH:mm");
    }

    return null;
  }

  private normalizeDateOnly(value: any): moment.Moment | null {
    if (value === null || value === undefined || value === "") {
      return null;
    }

    let parsed: moment.Moment | null = null;

    if (moment.isMoment(value)) {
      parsed = value.clone();
    } else if (value instanceof Date) {
      parsed = moment(value);
    } else if (typeof value === "string") {
      const trimmed = value.trim();
      if (trimmed !== "") {
        const strictParsed = moment.parseZone(
          trimmed,
          ["YYYY-MM-DD", moment.ISO_8601],
          true
        );
        if (strictParsed.isValid()) {
          parsed = strictParsed;
        }
      }
    }

    if (parsed && parsed.isValid()) {
      // Keep the same calendar day when serializing, avoid timezone shifts
      return parsed.startOf("day").utcOffset(0, true);
    }

    return null;
  }

  private normalizeTimeValue(value: any): string | null | false {
    if (value === null || value === undefined || value === "") {
      return null;
    }

    if (moment.isMoment(value)) {
      return value.clone().format("HH:mm");
    }

    if (value instanceof Date) {
      return moment(value).format("HH:mm");
    }

    if (typeof value !== "string") {
      return false;
    }

    const trimmed = value.trim();
    if (trimmed === "") {
      return null;
    }

    let normalized = trimmed.toLowerCase();
    normalized = normalized.replace(/[–—]/g, "-");
    normalized = normalized.replace(/\b(ore|ora|alle|h)\b/g, " ");
    normalized = normalized.replace(/\s+/g, " ");
    normalized = normalized.replace(/,/g, ".");
    normalized = normalized.trim();

    if (normalized === "") {
      return null;
    }

    if (
      normalized.includes(" ") &&
      !normalized.includes("-") &&
      !normalized.includes("/")
    ) {
      const pieces = normalized.split(" ").filter((piece) => piece.length > 0);
      if (pieces.length > 1) {
        return false;
      }
    }

    const rawParts = normalized.split(/[-/]/);
    const parts = rawParts
      .map((part) => part.trim())
      .filter((part) => part.length > 0);

    const hasSeparator = normalized.includes("-") || normalized.includes("/");

    if (hasSeparator && parts.length !== rawParts.length) {
      return false;
    }

    if (parts.length === 0) {
      return null;
    }

    if (parts.length > 2) {
      return false;
    }

    const formattedParts: string[] = [];
    for (const part of parts) {
      const formatted = this.formatTimePart(part);
      if (!formatted) {
        return false;
      }
      formattedParts.push(formatted);
    }

    if (formattedParts.length === 2) {
      return `${formattedParts[0]} - ${formattedParts[1]}`;
    }

    return formattedParts[0];
  }

  onTimeFieldBlur(controlName: string): void {
    if (!this.form) {
      return;
    }

    const control = this.form.get(controlName);
    if (!control) {
      return;
    }

    const normalizedValue = this.normalizeTimeValue(control.value);
    if (normalizedValue === false) {
      return;
    }

    if (normalizedValue !== control.value) {
      control.setValue(normalizedValue, { emitEvent: false });
    }
  }

  private formatTimePart(part: string): string | null {
    let clean = part.replace(/[^\d:.]/g, "");
    if (clean === "") {
      return null;
    }

    clean = clean.replace(/\./g, ":");

    if (/^\d{1,2}$/.test(clean)) {
      const hour = parseInt(clean, 10);
      if (Number.isNaN(hour) || hour < 0 || hour > 23) {
        return null;
      }
      return `${this.formatHour(hour)}:00`;
    }

    if (/^\d{3,4}$/.test(clean)) {
      const raw = parseInt(clean, 10);
      if (Number.isNaN(raw)) {
        return null;
      }
      const hour = Math.floor(raw / 100);
      const minute = raw % 100;
      if (hour < 0 || hour > 23 || minute < 0 || minute > 59) {
        return null;
      }
      return `${this.formatHour(hour)}:${minute.toString().padStart(2, "0")}`;
    }

    if (/^\d{1,2}:\d{1,2}$/.test(clean)) {
      const [hourStr, minuteStr] = clean.split(":");
      const hour = parseInt(hourStr, 10);
      const minute = parseInt(minuteStr, 10);
      if (
        Number.isNaN(hour) ||
        Number.isNaN(minute) ||
        hour < 0 ||
        hour > 23 ||
        minute < 0 ||
        minute > 59
      ) {
        return null;
      }
      return `${this.formatHour(hour)}:${minute.toString().padStart(2, "0")}`;
    }

    if (/^\d{1,2}:\d{1,2}:\d{1,2}$/.test(clean)) {
      const [hourStr, minuteStr, secondStr] = clean.split(":");
      const hour = parseInt(hourStr, 10);
      const minute = parseInt(minuteStr, 10);
      const second = parseInt(secondStr, 10);
      if (
        Number.isNaN(hour) ||
        Number.isNaN(minute) ||
        Number.isNaN(second) ||
        hour < 0 ||
        hour > 23 ||
        minute < 0 ||
        minute > 59 ||
        second < 0 ||
        second > 59
      ) {
        return null;
      }
      return `${this.formatHour(hour)}:${minute.toString().padStart(2, "0")}`;
    }

    return null;
  }

  private formatHour(hour: number): string {
    return hour.toString();
  }

  private formatDate(dateValue: any): string {
    let date: Date;

    if (dateValue instanceof Date) {
      date = dateValue;
    } else if (dateValue && dateValue.toDate) {
      date = dateValue.toDate();
    } else if (dateValue && typeof dateValue === "string") {
      date = new Date(dateValue);
    } else {
      date = new Date(dateValue);
    }

    return date.toLocaleDateString("it-IT");
  }

  checkRiservaOnDateChange(fieldName: string, dateValue: any): any {
    const riservaValue = this.form.value?.riserva_di_danno_range;
    if (!riservaValue || typeof riservaValue !== "string") {
      return null;
    }

    const trimmedValue = riservaValue.trim();

    // Estrai il primo numero (minimo nel caso di range)
    let numberToCheck = "";

    // Verifica se è un range con trattino
    const dashMatch = trimmedValue.match(/^(\d{1,3}(?:\.\d{3})*,\d{2})\s*-\s*/);
    if (dashMatch) {
      numberToCheck = dashMatch[1];
    }
    // Verifica se è un range con slash
    else {
      const slashMatch = trimmedValue.match(
        /^(\d{1,3}(?:\.\d{3})*,\d{2})\s*\/\s*/
      );
      if (slashMatch) {
        numberToCheck = slashMatch[1];
      } else {
        // È un numero singolo
        numberToCheck = trimmedValue;
      }
    }

    // Converti il numero in formato numerico
    const numericValue = this.parseItalianNumber(numberToCheck);

    // Se la riserva è superiore a 10.000, restituisci la data cambiata
    if (numericValue >= 10000) {
      const currentNote = this.form.value.note || "";
      const newNote = `${currentNote}${
        currentNote ? "\n" : ""
      }Caricata interlocutoria e mandata mail al liquidatore in data ${this.formatDate(
        dateValue
      )}`;

      this.form.patchValue({ note: newNote });
    }
  }

  private parseItalianNumber(value: string): number {
    if (!value) return 0;

    // Rimuovi i punti (separatori migliaia) e sostituisci la virgola con il punto
    const cleanValue = value.replace(/\./g, "").replace(",", ".");
    return parseFloat(cleanValue) || 0;
  }

  async handleSuccessfulSubmit() {
    try {
      if (!this.isRiservaDannoFormatValid()) {
        this.toast.error("Riserva di danno non è formattato correttamente");
        return;
      }

      // Blocca il salvataggio se data sopralluogo è valorizzata ma riserva di danno è vuota
      const riservaDiDannoRange = this.form.get("riserva_di_danno_range");
      if (riservaDiDannoRange?.invalid) {
        riservaDiDannoRange.markAsTouched();
        this.toast.error("Compilare 'Riserva di Danno' quando la Data Sopralluogo è valorizzata");
        return;
      }

      const formData = { ...this.form.value };

      formData.data_sopralluogo_fissata_data = this.normalizeDateOnly(
        formData.data_sopralluogo_fissata_data
      );
      const normalizedTime = this.normalizeTimeValue(
        formData.data_sopralluogo_fissata_ora
      );
      if (normalizedTime === false) {
        this.toast.error(
          "Formato orario non valido. Usa 'HH:MM' oppure 'HH:MM - HH:MM'"
        );
        return;
      }
      formData.data_sopralluogo_fissata_ora = normalizedTime;

      if (this.incarico?.id) {
        if (this.form.get("inviare_atto")?.value)
          await this.removeWorkflowStep([100]);

        Object.assign(this.incarico, formData);
        await this.incaricoService.edit(this.incarico);

        this.reloadCurrentRoute();
      } else {
        let res = await this.incaricoService.create(formData);
        this.router.navigate(["/incarichi/", res.id], {
          relativeTo: this.activatedRoute,
        });
      }

      if (
        this.incarico?.stato_riparazione_id != 8 &&
        formData?.stato_riparazione?.id == 8
      ) {
        await this.addToWorkflowStep(70);
        await this.ngOnInit();
      } else if (
        this.incarico?.stato_riparazione_id == 8 &&
        formData?.stato_riparazione?.id != 8
      ) {
        await this.removeWorkflowStep([70]);
        await this.ngOnInit();
      }

      this.toast.success(`Incarico salvato`);
    } catch (e) {
      this.toast.error("Impossibile salvare l'incarico");
      console.error(e);
    }
  }

  getDurataIncarico() {
    const values = this.form?.value;

    const start = values.data;
    if (!start) return;

    const end = values.data_spedizione;
    if (!end) return;

    return end.diff(start, "days");
  }

  async showDeepPropertyPdf() {
    const confirmed = window.confirm(
      "Sei sicuro di voler procedere all'analisi dell'indirizzo?"
    );
    if (confirmed) {
      if (
        this.form?.get("via")?.value != null &&
        this.form?.get("civico")?.value != null &&
        this.form?.get("comune")?.value != null
      ) {
        this.loading = true;

        const response: any = await this.http
          .get("assets/comuni.json")
          .toPromise();

        Object.values(response).forEach((comune: any) => {
          this.comuniByName.set(comune.denominazione_ita.toLowerCase(), comune);
        });

        let address =
          this.form?.get("via")?.value +
          ", " +
          this.form?.get("civico")?.value +
          ", " +
          this.form?.get("comune")?.value["cap"] +
          " " +
          this.form?.get("comune")?.value["nome"] +
          " " +
          this.comuniByName.get(
            this.form?.get("comune").value["nome"].toLowerCase()
          )["sigla_provincia"];

        const process = await this.deepPropertyService.processAddress(
          address,
          this.incarico.id
        );
        if (process?.error) {
          this.loading = false;
          this.toast.error("Report già presente nei files", "Errore!");
          return;
        }

        this.deepPropertyService.pollStatus(process.id, 24, 5000).subscribe({
          next: (response) => {
            if (response.status === "DONE" || response.status === 200) {
              this.deepPropertyService.getPDF(process.id, this.incarico.id);

              this.toast.success(
                "Il report è stato aggiunto ai files!",
                "Successo!"
              );

              this.loading = false;
            }
          },
          error: (error) => {},
          complete: () => {},
        });
      } else {
        this.toast.error("L'indirizzo non è completo!", "Errore!");
      }
    }
  }

  /**
   * Stefano ha richiesto che quando la data di sopralluogo viene valorizzata, bisogna mettere anche una riserva di danno (da 0 a 999999999999)
   * Il template (.html) gestisce l'aspetto del required condizionale in base al valore della data sopralluogo,
   * però dobbiamo marcare l'input della riserva di danno come dirty altrimenti non viene visualizzato l'errore
   */
  private setupInteractionBetweenDataSopralluogoAndRiservaDiDanno() {
    const riservaDiDannoRange = this.form.controls.riserva_di_danno_range;

    // Stato iniziale: se data_sopralluogo è già valorizzata, aggiungi il validatore
    if (this.form.controls.data_sopralluogo.value) {
      riservaDiDannoRange.setValidators([Validators.required]);
      riservaDiDannoRange.updateValueAndValidity({ emitEvent: false });
    }

    //quando la data di sopralluogo cambia...
    this.form.controls.data_sopralluogo.valueChanges.subscribe((v) => {
      if (v) {
        // se la nuova data di sopralluogo è truthy, aggiungi Validators.required
        riservaDiDannoRange.setValidators([Validators.required]);
        riservaDiDannoRange.markAsTouched();
      } else {
        // se viene rimossa, togli il validatore
        riservaDiDannoRange.clearValidators();
      }
      riservaDiDannoRange.updateValueAndValidity();
    });
  }

  // Disables/enables 'data_sopralluogo' based on 'data_primo_contatto'
  private setupDataSopralluogoDependency() {
    const dataSopralluogoCtrl = this.form.get("data_sopralluogo");
    const dataPrimoContattoCtrl = this.form.get("data_primo_contatto");

    if (!dataSopralluogoCtrl || !dataPrimoContattoCtrl) return;

    // If user cannot modify 'data_sopralluogo' due to permission and it already has a value, keep it disabled
    const hasEditRestriction =
      !this.loggedInUser.hasRole("Può modificare data sopralluogo") &&
      !!this.incarico?.data_sopralluogo;

    const applyToggle = () => {
      if (hasEditRestriction) {
        dataSopralluogoCtrl.disable({ emitEvent: false });
        this.dataSopralluogoDisabledReason =
          "Campo bloccato: non hai i permessi per modificare la Data Sopralluogo";
        return;
      }
      const hasPrimoContatto = !!dataPrimoContattoCtrl.value;
      if (hasPrimoContatto) {
        dataSopralluogoCtrl.enable({ emitEvent: false });
        this.dataSopralluogoDisabledReason = null;
      } else {
        dataSopralluogoCtrl.disable({ emitEvent: false });
        this.dataSopralluogoDisabledReason = "Compilare 'Data 1° Contatto'";
      }
    };

    // Initial state
    applyToggle();

    // React to changes on first contact date
    dataPrimoContattoCtrl.valueChanges.subscribe(() => applyToggle());
  }

  private setupDataScadenzaObserver() {
    this.form.controls.data.valueChanges.subscribe((dataValue) => {
      if (dataValue) {
        const newDataScadenza = moment(dataValue).add(30, "days");
        this.form.patchValue(
          { data_scadenza: newDataScadenza },
          { emitEvent: false }
        );
      }
    });
  }

  reloadCurrentRoute(navigationState?: any) {
    const currentUrl = this.router.url;
    const baseState = this.isInseritoDaPortale
      ? { inseritoDaPortale: true }
      : {};
    const mergedState = { ...baseState, ...(navigationState || {}) };
    const stateToPass =
      Object.keys(mergedState).length > 0 ? mergedState : undefined;

    this.skipNavigationCheckOnce = true;
    this.router.navigateByUrl("/", { skipLocationChange: true }).then(() => {
      if (stateToPass) {
        this.router.navigate([currentUrl], { state: stateToPass });
      } else {
        this.router.navigate([currentUrl]);
      }
    });
  }

  private shouldCheckHomeNavigation(
    targetUrl: string | null | undefined
  ): boolean {
    if (!this.incarico?.id) return false;
    const normalizedTarget = (targetUrl || "").split("?")[0];
    return normalizedTarget === "/home" || normalizedTarget.startsWith("/home");
  }

  private async loadFilesForCurrentIncarico(): Promise<IncaricoFile[]> {
    try {
      if (!this.incarico?.id) {
        return [];
      }
      const files =
        (await this.fileService.getAll(
          [],
          {
            desiresPagination: false,
            search: { incarico_id: this.incarico.id },
          },
          { cancelPrevious: false }
        )) || [];
      return files;
    } catch (e) {
      console.error("Errore durante il controllo dei files:", e);
      return [];
    }
  }

  async confirmNavigationToHome(
    targetUrl: string | null | undefined
  ): Promise<boolean> {
    if (this.skipNavigationCheckOnce) {
      this.skipNavigationCheckOnce = false;
      return true;
    }

    if (!this.shouldCheckHomeNavigation(targetUrl)) {
      return true;
    }

    const files = await this.loadFilesForCurrentIncarico();
    const fileCount = files?.length ?? 0;
    const hasFrontespizio = (files || []).some((file) =>
      (file?.nome || "").toLowerCase().includes("frontespizio interno")
    );

    const issues: string[] = [];
    if (!hasFrontespizio) {
      issues.push("manca un file con nome contenente 'frontespizio interno'");
    }
    if (fileCount < 2) {
      issues.push(`sono presenti solo ${fileCount} file (minimo 2 richiesti)`);
    }

    if (!issues.length) {
      return true;
    }

    const message =
      issues.length === 1
        ? `Attenzione: ${issues[0]}. Vuoi andare comunque alla Home?`
        : `Attenzione: ${issues.join(" e ")}. Vuoi andare comunque alla Home?`;

    return window.confirm(message);
  }

  /** mappa da id stato a ' sta venendo fatta una richiesta per passare a quello stato? ' */
  loadingActions = {
    10: false,
    11: false,
    13: false,
  };

  async cambiaStato(stato_id, stato_step_name) {
    this.loadingActions[stato_id] = true;
    try {
      this.form.value.stato.id = stato_id;
      await this.handleSuccessfulSubmit();

      // Quando si passa a "Incarico da Evadere" rimuovi l'incarico dai workflow step correlati
      if (stato_id === 11) {
        await this.removeWorkflowStep([90, 100, 110, 130]);
      }
    } finally {
      this.loadingActions[stato_id] = false;
    }
  }

  async copyDataFromPolizza() {
    // Try to obtain contraente from selected polizza or current incarico
    const selectedPolizza =
      this.form?.value?.polizza?.original_model ?? this.form?.value?.polizza;
    let contraente =
      selectedPolizza?.contraente ?? this.incarico?.polizza?.contraente;
    let fullPolizza: any;

    // Debug snapshot removed

    // Patch basic address fields immediately
    this.form.patchValue({
      via: contraente?.via,
      civico: contraente?.civico,
      cap: contraente?.cap ?? contraente?.comune?.cap ?? "",
    });

    // Ensure 'comune' object is available (some pickers may not include relations)
    let comune = contraente?.comune;
    let possibileComuneId: number | undefined = (contraente as any)?.comune_id;

    // If contraente is missing or lacks comune info, reload polizza with required relations
    try {
      const polizzaId = selectedPolizza?.id;
      if ((!contraente || (!comune && !possibileComuneId)) && polizzaId) {
        fullPolizza = await this.polizzaService.getOne(polizzaId, [
          "contraente",
          "contraente.comune",
        ]);
        contraente = fullPolizza?.contraente || contraente;
        comune = contraente?.comune || comune;
        possibileComuneId = possibileComuneId ?? (contraente as any)?.comune_id;

        // debug toast removed
      }
    } catch (e) {
      console.error(
        "Impossibile caricare la polizza completa per il contraente:",
        e
      );
      // debug toast removed
    }

    try {
      if (!comune && possibileComuneId) {
        // Load comune by id when relation isn't present in the selected polizza
        comune = await this.comuneService.getOne(possibileComuneId);
      }
    } catch (e) {
      console.error("Impossibile recuperare il comune del contraente:", e);
    }

    // Apply comune if available, normalizing to the picker's parts shape
    if (comune) {
      const normalizedComune = {
        id: (comune as any)?.id,
        humanIdentifier:
          (comune as any)?.humanIdentifier || (comune as any)?.nome || "",
        nome: (comune as any)?.nome || "",
      } as any;

      const comuneControl = this.form.get("comune");
      if (comuneControl) {
        comuneControl.setValue(normalizedComune);
        comuneControl.markAsDirty();
        comuneControl.updateValueAndValidity({ emitEvent: true });
      } else {
        this.form.patchValue({ comune: normalizedComune });
      }

      // debug toast removed

      // If cap is missing, try infer from comune
      if (!this.form?.value?.cap && (comune as any)?.cap) {
        this.form.patchValue({ cap: (comune as any).cap });
      }
      try {
        (this.incarico as any).comune_id = (comune as any)?.id;
      } catch {}
    } else {
      // debug toast removed
    }

    const agenzia = selectedPolizza?.agenzia ?? fullPolizza?.agenzia ?? null;
    const contraenteUpdated = this.applyContraenteToReadOnlyForm(
      contraente,
      agenzia
    );
    if (!contraenteUpdated) {
      this.refreshContraenteMissingFields();
    }
  }

  private setupContraenteSync(): void {
    try {
      const contraenteCtrl = this.readOnlyForm?.get("contraente");
      contraenteCtrl?.valueChanges.subscribe(() => {
        this.refreshContraenteMissingFields();
      });

      const polizzaCtrl = this.form?.get("polizza");
      if (polizzaCtrl) {
        this.updateReadOnlyContraenteFromPolizza(polizzaCtrl.value, true);
        polizzaCtrl.valueChanges.subscribe((value: any) => {
          this.updateReadOnlyContraenteFromPolizza(value);
        });
      } else {
        this.refreshContraenteMissingFields();
      }

      // Ensure initial state is computed even if controls do not emit
      this.refreshContraenteMissingFields();
    } catch (error) {
      console.error(
        "Errore durante la sincronizzazione del contraente:",
        error
      );
      this.refreshContraenteMissingFields();
    }
  }

  private updateReadOnlyContraenteFromPolizza(
    polizzaValue: any,
    forceRefresh: boolean = false
  ): void {
    const normalizedPolizza = this.normalizePolizza(polizzaValue);
    const contraente = normalizedPolizza?.contraente ?? null;
    const agenzia = normalizedPolizza?.agenzia ?? null;

    const changed = this.applyContraenteToReadOnlyForm(contraente, agenzia);
    this.triggerRefreshIncaricatoPreferenceHints(
      normalizedPolizza,
      this.readOnlyForm?.value?.amministratore
    );
    if (!changed || forceRefresh) {
      this.refreshContraenteMissingFields();
    }
  }

  private setupAutoGlobaleFabbricatiToggle(): void {
    const polizzaCtrl = this.form?.get("polizza");
    const globaleCtrl = this.form?.get("globale_fabbricati");
    if (!polizzaCtrl || !globaleCtrl) {
      return;
    }

    const applyRule = (polizzaValue: any) => {
      const normalizedPolizza = this.normalizePolizza(polizzaValue);
      const contraente = normalizedPolizza?.contraente ?? null;
      const contraenteType = this.resolveContraenteType(contraente);
      const nameCandidates = [
        (contraente as any)?.nome,
        (contraente as any)?.denominazione,
        (contraente as any)?.ragione_sociale,
        (contraente as any)?.humanIdentifier,
      ];
      const rawName =
        nameCandidates.find(
          (candidate) =>
            typeof candidate === "string" && candidate.trim().length > 0
        ) ?? "";
      const normalizedName = rawName.toString().toLowerCase();
      const shouldSelect =
        contraenteType === "fabbricato" && normalizedName.startsWith("cond");

      if (globaleCtrl.value !== shouldSelect) {
        globaleCtrl.setValue(shouldSelect, { emitEvent: false });
        globaleCtrl.markAsDirty();
      }
    };

    // Only auto-apply on initial load for new incarichi;
    // for existing ones, respect the saved DB value.
    if (!this.incarico?.id) {
      applyRule(polizzaCtrl.value);
    }
    polizzaCtrl.valueChanges.subscribe(applyRule);
  }

  private applyContraenteToReadOnlyForm(
    contraente: any,
    agenzia: any = undefined
  ): boolean {
    if (!this.readOnlyForm) return false;

    const patch: any = {};
    let changed = false;

    const contraenteControl = this.readOnlyForm.get("contraente");
    if (
      contraenteControl &&
      !this.areEntitiesSame(contraenteControl.value, contraente)
    ) {
      patch.contraente = contraente ?? null;
      changed = true;
    }

    if (agenzia !== undefined) {
      const agenziaControl = this.readOnlyForm.get("agenzia");
      if (
        agenziaControl &&
        !this.areEntitiesSame(agenziaControl.value, agenzia)
      ) {
        patch.agenzia = agenzia ?? null;
        changed = true;
      }
    }

    if (changed) {
      this.readOnlyForm.patchValue(patch);
    }

    return changed;
  }

  private refreshContraenteMissingFields(): void {
    this.contraenteMissingFieldsState = this.computeContraenteMissingFields();
  }

  getContraenteMissingFields(): string[] {
    return this.contraenteMissingFieldsState;
  }

  private computeContraenteMissingFields(): string[] {
    try {
      if (!this.hasAmministrativo) {
        return [];
      }
      const contraente = this.resolveCurrentContraente();
      const type = this.resolveContraenteType(contraente);
      if (!contraente || !type) return [];

      const missing: string[] = [];

      const hasValue = (...keys: string[]): boolean => {
        for (const key of keys) {
          if (!key) continue;
          const value = contraente?.[key];
          if (value === null || value === undefined) continue;
          if (typeof value === "string") {
            if (value.trim().length > 0) return true;
            continue;
          }
          if (typeof value === "number") {
            if (!isNaN(value)) return true;
            continue;
          }
          if (typeof value === "object") {
            if (Array.isArray(value)) {
              if (value.length > 0) return true;
            } else if (Object.keys(value).length > 0) {
              return true;
            }
            continue;
          }
          if (value) return true;
        }
        return false;
      };

      const hasComune = (): boolean => {
        const comune = contraente?.comune;
        if (typeof comune === "string") {
          if (comune.trim().length > 0) return true;
        } else if (comune) {
          if (
            (comune as any)?.id != null ||
            (typeof (comune as any)?.nome === "string" &&
              (comune as any).nome.trim().length > 0)
          ) {
            return true;
          }
        }
        const comuneId =
          (contraente as any)?.comune_id ?? (contraente as any)?.comuneId;
        return comuneId != null && `${comuneId}`.trim().length > 0;
      };

      const requireField = (label: string, ...keys: string[]) => {
        if (!hasValue(...keys)) missing.push(label);
      };

      const hasCivicNumber = (): boolean => {
        if (hasValue("civico")) return true;
        const street = contraente?.via;
        if (typeof street === "string") {
          if (/\d+/.test(street)) return true;
        }
        return false;
      };

      const ensureTaxId = () => {
        const hasPartitaIva = hasValue("partita_iva", "partitaIva");
        const hasCodiceFiscale = hasValue("codice_fiscale", "codiceFiscale");
        if (!hasPartitaIva && !hasCodiceFiscale) {
          missing.push("partita IVA o codice fiscale");
        }
      };

      if (type === "altroSoggetto") {
        requireField("nome", "nome");
        requireField("via", "via");
        if (!hasCivicNumber()) missing.push("civico");
        if (!hasComune()) missing.push("comune");
        requireField("cap", "cap");
        ensureTaxId();
      } else if (type === "fabbricato") {
        requireField("nome", "nome");
        requireField("via", "via");
        if (!hasCivicNumber()) missing.push("civico");
        if (!hasComune()) missing.push("comune");
        requireField("cap", "cap");
        ensureTaxId();
      }

      return missing;
    } catch {
      return [];
    }
  }

  private normalizePolizza(polizza: any): any {
    if (!polizza) return null;
    return polizza?.original_model ?? polizza;
  }

  private triggerRefreshIncaricatoPreferenceHints(
    polizzaValue?: any,
    amministratoreValue?: any
  ): void {
    this.refreshIncaricatoPreferenceHints(polizzaValue, amministratoreValue);
  }

  private async refreshIncaricatoPreferenceHints(
    polizzaValue?: any,
    amministratoreValue?: any
  ): Promise<void> {
    const requestId = ++this.preferenceHintsRequestId;
    try {
      let polizza = this.normalizePolizza(
        polizzaValue ?? this.form?.value?.polizza
      );
      const polizzaId = polizza?.id;

      let agenziaId = polizza?.agenzia?.id ?? polizza?.agenzia_id;
      let compagniaId = polizza?.compagnia?.id ?? polizza?.compagnia_id;
      let amministratore =
        amministratoreValue ??
        this.readOnlyForm?.value?.amministratore ??
        polizza?.contraente?.amministratore;
      let amministratoreId = amministratore?.id;

      if (polizzaId && (!agenziaId || !compagniaId || !amministratoreId)) {
        try {
          polizza = await this.polizzaService.getOne(polizzaId, [
            "compagnia",
            "agenzia",
            "contraente",
            "fabbricato",
            "fabbricato.amministratore",
          ]);
          agenziaId = polizza?.agenzia?.id ?? polizza?.agenzia_id;
          compagniaId = polizza?.compagnia?.id ?? polizza?.compagnia_id;
          amministratore =
            amministratore ??
            polizza?.fabbricato?.amministratore ??
            (polizza?.contraente as any)?.amministratore;
          amministratoreId = amministratore?.id;
        } catch (e) {
          console.error(
            "Impossibile caricare dettagli polizza per preferenze:",
            e
          );
        }
      }

      const hints: { [userId: number]: string[] } = {};

      if (agenziaId) {
        const prefsAgenzie =
          (await this.preferenzaAgenziaService.getAll(
            ["incaricato", "agenzia"],
            {
              desiresPagination: false,
              search: { agenzia_id: agenziaId },
            } as any
          )) || [];

        for (const pref of prefsAgenzie) {
          const userId = pref?.incaricato?.id ?? pref?.incaricato_id;
          if (!userId) continue;
          const agenziaName = pref?.agenzia?.humanIdentifier;
          const message = agenziaName
            ? `Preferito dall'agenzia ${agenziaName}`
            : "Preferito dall'agenzia";
          hints[userId] = hints[userId] || [];
          hints[userId].push(message);
        }
      }

      if (amministratoreId && compagniaId) {
        const prefsAmm =
          (await this.preferenzaAmministratoreService.getAll(
            ["incaricato", "compagnia", "altro_soggetto"],
            {
              desiresPagination: false,
              search: {
                altro_soggetto_id: amministratoreId,
                compagnia_id: compagniaId,
              },
            } as any
          )) || [];

        for (const pref of prefsAmm) {
          const userId = pref?.incaricato?.id ?? pref?.incaricato_id;
          if (!userId) continue;
          const adminName = pref?.altro_soggetto?.humanIdentifier;
          const compName = pref?.compagnia?.humanIdentifier;
          const detailParts = [];
          if (adminName) detailParts.push(`amministratore ${adminName}`);
          if (compName) detailParts.push(`compagnia ${compName}`);
          const details = detailParts.length
            ? ` (${detailParts.join(" - ")})`
            : "";
          const message = `Preferito per${details || " amministratore"}`;
          hints[userId] = hints[userId] || [];
          hints[userId].push(message);
        }
      }

      if (requestId === this.preferenceHintsRequestId) {
        this.incaricatoPreferenceHints = hints;
      }
    } catch (error) {
      console.error("Errore nel recupero preferenze incaricati:", error);
    }
  }

  private areEntitiesSame(a: any, b: any): boolean {
    if (a === b) return true;
    if (!a && !b) return true;
    if (!a || !b) return false;

    const aId = this.extractEntityId(a);
    const bId = this.extractEntityId(b);

    if (aId != null || bId != null) {
      if (aId == null || bId == null) {
        return false;
      }
      if (aId !== bId) {
        return false;
      }
      return a === b;
    }

    return false;
  }

  private extractEntityId(entity: any): number | string | null {
    if (!entity || typeof entity !== "object") {
      return null;
    }

    return (
      (entity as any)?.id ??
      (entity as any)?.original_model?.id ??
      (entity as any)?.value?.id ??
      null
    );
  }

  getCurrentContraenteType(): string | null {
    try {
      const contraente = this.resolveCurrentContraente();
      return this.resolveContraenteType(contraente);
    } catch {
      return null;
    }
  }

  private resolveCurrentContraente(): any {
    return (
      this.form?.value?.polizza?.original_model?.contraente ??
      this.form?.value?.polizza?.contraente ??
      this.readOnlyForm?.value?.contraente ??
      this.incarico?.polizza?.contraente ??
      null
    );
  }

  private resolveContraenteType(contraente: any): string | null {
    const typeCandidates = [
      contraente?.type,
      contraente?.contraente_type,
      this.form?.value?.polizza?.original_model?.contraente?.type,
      this.form?.value?.polizza?.contraente?.type,
      this.form?.value?.polizza?.contraente_type,
      this.incarico?.polizza?.contraente?.type,
      this.incarico?.polizza?.contraente_type,
    ];

    for (const candidate of typeCandidates) {
      if (typeof candidate !== "string") continue;
      const normalized = candidate.includes("AltroSoggetto")
        ? "altroSoggetto"
        : candidate.includes("Fabbricato")
        ? "fabbricato"
        : candidate;
      if (normalized) return normalized;
    }

    if (contraente && typeof contraente === "object") {
      if ("cognome" in contraente) return "altroSoggetto";
      if ("amministratore" in contraente) return "fabbricato";
    }

    return null;
  }

  getSinistroInfo() {
    const i = this.incarico;
    const data = i?.data_sinistro;
    // Sin.n° 005I162022014371857 GENERALI ITALIA S.p.A.del 13 / 01 / 2022 COND.VIA CAIROLI 8 – NS.RIF. 2129 - 22
    const subject = `Sin. n° ${i?.numero_sinistro} ${
      i?.polizza?.compagnia?.nome
    } del ${data?.format("DD/MM/YYYY") ?? "??/??/????"} ${
      i?.polizza?.contraente?.humanIdentifier
    } - NS.RIF. ${i?.humanIdentifier}`;

    return subject;
  }

  getMailHref() {
    return `mailto:${
      this.hasAmministrativo ? this.incarico?.incaricato?.email : ""
    }?subject=${encodeURIComponent(this.getSinistroInfo())}`;
  }

  getMailAttesaDoc() {
    const destinatario = `${this.incarico?.incaricato?.email || ""};${
      this.incarico?.polizza?.contraente?.email ||
      this.incarico?.polizza?.contraente?.amministratore?.email ||
      ""
    }`;
    return `mailto:${
      destinatario + `?cc=${this.incarico?.polizza?.agenzia?.email || ""}`
    }&subject=${encodeURIComponent(this.getSinistroInfo())}`;
  }

  getMailAttesaDocumentazione() {
    const destinatario = `${
      this.incarico?.polizza?.contraente?.email ||
      this.incarico?.polizza?.contraente?.amministratore?.email ||
      ""
    }`;

    const i = this.incarico;
    const data = i?.data_sinistro;

    let controparteName: string | null = null;
    try {
      const cp = Array.isArray(i?.controparti) ? i.controparti[0] : null;
      controparteName =
        (cp as any)?.getHumanIdentifier?.() ||
        (cp as any)?.humanIdentifier ||
        (cp as any)?.soggetto?.humanIdentifier ||
        (cp as any)?.soggetto?.nome ||
        null;
    } catch {}

    const subject = `Sin. n° ${i?.numero_sinistro} del ${
      data?.format("DD/MM/YYYY") ?? "??/??/????"
    } - Assicurazione: ${i?.polizza?.compagnia?.nome} - Assicurato: ${
      i?.polizza?.contraente?.humanIdentifier
    }${controparteName ? ` - Controparte: ${controparteName}` : ""} - NS.RIF. ${
      i?.humanIdentifier
    }`;

    return `mailto:${
      destinatario + `?cc=${this.incarico?.polizza?.agenzia?.email || ""}`
    }&subject=${encodeURIComponent(subject)}`;
  }

  getMailSollecito() {
    const destinatario = this.incarico?.incaricato?.email || "";
    const sinistroInfo = this.getSinistroInfo();
    const subject = sinistroInfo ? encodeURIComponent(sinistroInfo) : "";
    return `mailto:${destinatario}?subject=${subject}`;
  }

  copySollecitoText() {
    const text = `Aggiornamento Stato Sinistro\n\nGrazie`;

    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text);
      }
    } catch (err) {
      console.error("Failed to copy text: ", err);
    }
  }

  async updateIncaricoSollecitato(): Promise<void> {
    this.copySollecitoText();

    this.form.patchValue({ sollecitato: true });
    Object.assign(this.incarico, this.form.value);

    await this.incaricoService.edit(this.incarico);
    this.reloadCurrentRoute();
  }

  async updateIncaricoNote(
    text: string,
    prependDate: boolean = false
  ): Promise<void> {
    const currentNote = this.form.value.note || "";
    const dateString = new Date().toLocaleDateString("it-IT");
    const formattedText = prependDate
      ? `${dateString} ${text}`
      : `${text}${dateString}`;
    const newNote = `${currentNote}${currentNote ? "\n" : ""}${formattedText}`;

    this.form.patchValue({ note: newNote });
    Object.assign(this.incarico, this.form.value);

    await this.incaricoService.edit(this.incarico);
    this.reloadCurrentRoute();
  }

  async inviaIncarico() {
    if (window.confirm("Vuoi inviare l'incarico a Generali?")) {
      const i = this.incarico;
      const id = i?.id;
      let params = {
        studio_id: this.loggedInUser.studio_id,
        slug: extensions.generali,
      };
      this.loading = true;
      try {
        let res = await this.estensioneService.inviaIncarico(id, params);
        this.toast.success(`Incarico inviato correttamente`);
      } catch (e) {
        console.log(e);

        if (e.error) {
          let error_result = ErrorHandler.parseError(e.error);
          alert(ErrorHandler.formatError(error_result));
        }
      } finally {
        this.loading = false;
      }
    }
  }

  sendWhatsappMessage() {
    const phoneNumber = (
      this.incarico?.polizza?.contraente?.cell ??
      this.incarico?.polizza?.contraente?.tel
    ).replace(/\s/g, "");
    const message =
      "*1° Contatto Studio Peritek Srl*\n" +
      this.getSinistroInfo() +
      "\n\nProvvederà a contattarVi un ns. operatore al fine di concordare sopralluogo e procedere con l'espletamento delle operazioni richieste.\n" +
      "Si richiede anticipatamente di fornire la documentazione inerente i danni lamentati (doc. contabile e/o fotografica) *esclusivamente* alla mail del collaboratore di riferimento:\n\n" +
      "*" +
      this.incarico?.incaricato?.humanIdentifier +
      "*\n" +
      this.incarico?.incaricato?.email;

    if (phoneNumber)
      window.open(
        `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`
      );
  }

  private setupViaCapitalization(): void {
    this.form.get("via")?.valueChanges.subscribe((value: string) => {
      if (!value) return;
      const capitalized = this.capitalizeEachWord(value);
      if (capitalized !== value) {
        this.form.get("via").setValue(capitalized, { emitEvent: false });
      }
    });
  }

  private capitalizeEachWord(value: string): string {
    return value.replace(/\S+/g, (word) => {
      if (this.isRomanNumeral(word)) {
        return word.toUpperCase();
      }
      const firstChar = word.charAt(0).toUpperCase();
      return firstChar + word.slice(1).toLowerCase();
    });
  }

  private isRomanNumeral(token: string): boolean {
    if (token.length < 2) return false;
    const upper = token.toUpperCase();
    const excluded = ["DI", "CI", "LI", "MI"];
    if (excluded.includes(upper)) return false;
    return /^M{0,3}(CM|CD|D?C{0,3})(XC|XL|L?X{0,3})(IX|IV|V?I{0,3})$/i.test(
      token
    );
  }
}
